import { I18NextPipe } from 'angular-i18next';
import { TagColor } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants';
import {
  UnixAgentVersionSupportsOfflineLicenseManagement,
  WindowsAgentVersionSupportsOfflineLicenseManagement
} from 'mbs-ui-shared@constants/agent-versions';
import { getOfflineDuration } from 'mbs-ui-shared@utils/computer-utils';
import { BuildType, getBuildTypeKeysByValues } from './BuildType.enum';
import Company from './Company';
import { ComputerLicense, ComputerLicenseStatus, ComputerLicenseStatusMap, ComputerLicenseType, ExtendedComputerLicense } from './License';
import { PagedResponse, QueryPagingParams, QuerySortParams } from './Paging';
import { SidePanelUriParams } from './SidePanelUriParams';
import { ComputerPlanInfo } from './backup/plan-info-model';
import HostInfo from './rmm/HostInfo';
import { SidepanelRouteType } from './sidepanel';

export enum OsType {
  apple = 'apple',
  linux = 'linux',
  windows = 'windows',
  undefined = 'undefined'
}

export enum PlatformTypes {
  none = 0,
  crossPlatform = 1 << 0,
  netFramework = 1 << 1,
  DrLinux = 1 << 2
}

export enum OsTypeNumber {
  undefined = 0,
  windows = 1,
  linux = 2,
  apple = 3
}

export enum Platform {
  Desktop,
  Server
}

export enum DisplayOsType {
  apple = 'macOS',
  linux = 'linux',
  windows = 'windows'
}

export const OSTypeAdapter: { os: OsType; displayOS: DisplayOsType }[] = [
  { os: OsType.windows, displayOS: DisplayOsType.windows },
  { os: OsType.linux, displayOS: DisplayOsType.linux },
  { os: OsType.apple, displayOS: DisplayOsType.apple }
];

export enum AuthStatus {
  All = 'all',
  Pending = 'pending',
  Authorized = 'authorized'
}

export type ComputerPlanStatistic = {
  totalBackupError: number;
  totalBackupOverdue: number;
  totalBackupPending: number;
  totalBackupSuccess: number;
  totalBackupWarning: number;
  totalPlans: number;
  totalRestoreError: number;
  totalRestoreOverdue: number;
  totalRestorePending: number;
  totalRestoreSuccess: number;
  totalRestoreWarning: number;
  totalUndefinedPlanError: number;
  totalUndefinedPlanOverdue: number;
  totalUndefinedPlanPending: number;
  totalUndefinedPlanWarning: number;
};

const netBuildTypes = [BuildType.NetMacArm, BuildType.NetLinuxDeb, BuildType.NetLinuxRpm];

export const minimalWindowsVersionForNewAsync = 800;
export const minimalWindowsVersionForOfflineEdit = 793000;
export const minimalWindowsVersionForLogSummary = 865;
export const minimalAgentVersionForBootableUSB = 885;
export const minimalWindowsVersionForPlanWithDataDeletion = 810000;

export default class Computer {
  id?: string;
  hid?: string;
  name?: string;
  displayName?: string;
  company?: Company;
  displayVersion?: string;
  offlineDuration?: number;
  hidden?: boolean;
  /**
   * @deprecated
   */
  profileName?: string;

  planLastStatus?: {
    lastBackupSuccessDate: string;
    lastStatus: string;
    lastStatusDate: string;
  };

  userAccount?: {
    id?: string;
    accountName?: string;
    firstName?: string;
    lastName?: string;
  };

  os?: OsType;
  osType?: Platform;
  instanceType?: VMDetectorInstance;

  apps?: Agent[];
  ComputerName?: Computer['name'];
  IsOffline?: boolean | 'True' | 'False';
  /**
   * @deprecated use version from apps
   */
  Version?: string;

  plans?: ComputerPlanInfo[];
  hostInfo?: HostInfo;

  backupLicense?: ComputerLicense;
  rmmLicense?: ComputerLicense;
  connectLicense?: ComputerLicense;

  online?: boolean;

  lostAuth?: boolean;
  ipAddress?: string;

  healthState?: string;
  healthStateTime?: string; // dateTime
  tags?: ComputerTagDto[];
  computerTag?: string;
  computerName?: string;
  authStatus?: AuthStatus;
  autoUpdateDisabled?: boolean;
  socketsNumber?: number;
  timeZoneOffset?: number;
  planStatistic?: ComputerPlanStatistic;
  includedInRmmCe?: boolean;

  obsolete?: boolean;
  obsolescenceMark?: number;

  isBootableUsb?: boolean;
  lastLoggedUser?: string;

  constructor(init?: Partial<Computer>) {
    Object.assign(this, {}, init);
  }

  static isDisplayNameExist(computer: Computer): boolean {
    return !!computer?.displayName || false;
  }

  static getComputerName(computer: Computer) {
    return Computer.isDisplayNameExist(computer) ? `${computer.displayName} [${computer.name}]` : computer?.name || '';
  }

  static getComputerVmName(computer: Computer): string {
    const name = computer?.name || '';

    return (Computer.isDisplayNameExist(computer) ? `${computer.displayName}--${name}` : name).replaceAll(' ', '-');
  }

  static getComputerStatusText(online: boolean, i18nPipe: I18NextPipe): string {
    return i18nPipe.transform(`shared:computers.${online ? 'online' : 'offline'}`, { format: 'title' });
  }

  static getComputerStatusDurationText(computer: Computer, i18nPipe: I18NextPipe, delimiter = ':'): string {
    const status = Computer.getComputerStatusText(computer?.online, i18nPipe);
    const duration = computer?.offlineDuration ? `${delimiter} ${getOfflineDuration(computer.offlineDuration)}` : '';
    return `${status}${duration}`;
  }

  static getAgent(computer: Computer, agent: AgentType): Agent {
    return computer?.apps?.find((app) => app.applicationId === agent) ?? null;
  }

  static hasAgent(computer: Computer, agent: AgentType): boolean {
    return Boolean(Computer.getAgent(computer, agent));
  }

  static hasAgents(computer: Computer, agents: AgentType[]): boolean {
    return agents.every((agent) => Computer.hasAgent(computer, agent));
  }

  static isBetaAgent(computer: Computer, agent: AgentType): boolean {
    return Computer.getAgent(computer, agent)?.isBeta;
  }

  static isAgentOnline(computer: Computer, agent: AgentType): boolean {
    return computer?.apps?.find((app) => app.applicationId === agent)?.online ?? false;
  }

  static getBetaAgentExpiredDate(computer: Computer, agent: AgentType): string {
    return Computer.getAgent(computer, agent)?.betaExpiredDate;
  }

  static getAgentBuildType(computer: Computer, agent: AgentType): BuildType {
    const buildType = Computer.getAgent(computer, agent)?.buildType;
    return buildType ? BuildType[buildType] : null;
  }

  static isUltimateOrSQLServer(computer: Computer): boolean {
    return (computer?.apps || [])
      .map((agent: Agent) => agent.buildType)
      .some((appBuildType) => getBuildTypeKeysByValues([BuildType.SqlServer, BuildType.Ultimate]).includes(appBuildType));
  }

  static isUltimateOrAnySQL(computer: Computer, agent: AgentType = AgentType.Backup): boolean {
    return getBuildTypeKeysByValues([BuildType.SqlServer, BuildType.SqlExchange, BuildType.Ultimate]).includes(
      Computer.getAgent(computer, agent)?.buildType
    );
  }

  static hasOsType(computer: Computer, osTypes: OsType[]): boolean {
    return osTypes.includes(computer?.os);
  }

  static isWindows(computer: Computer): boolean {
    return Computer.hasOsType(computer, [OsType.windows]);
  }

  static isLinux(computer: Computer): boolean {
    return Computer.hasOsType(computer, [OsType.linux]);
  }

  static isServer(computer: Computer): boolean {
    return computer?.osType === Platform.Server;
  }

  static isMac(computer: Computer): boolean {
    return Computer.hasOsType(computer, [OsType.apple]);
  }

  static isNetBuild(computer: Computer, agentType = AgentType.Backup): boolean {
    const agent = Computer.getAgent(computer, agentType);

    return agent && (netBuildTypes.includes(BuildType[agent.buildType]) || netBuildTypes.includes(+agent.buildType));
  }

  static isDRServer(computer: Computer, agentType = AgentType.Backup): boolean {
    const agent = Computer.getAgent(computer, agentType);

    return agent && (BuildType[agent.buildType] === BuildType.DrLinuxDeb || +agent.buildType === BuildType.DrLinuxDeb);
  }

  static isUnix(computer: Computer): boolean {
    return Computer.hasOsType(computer, [OsType.linux, OsType.apple]);
  }

  static getAgentVersion(computer: Computer, agentType: AgentType, needFull = false): number {
    const versionString = Computer.getAgent(computer, agentType)?.version?.split('.').join('');
    let version = versionString?.substring(0, 3);

    if (needFull && versionString) {
      let floatSlice = versionString.substring(3, 6);

      if (floatSlice.length === 1) floatSlice = `00${floatSlice}`;
      if (floatSlice.length === 2) floatSlice = `0${floatSlice}`;

      version += `${floatSlice}`;
    }

    return +version || 0;
  }

  static getAgentVersionStatus(computer: Computer, agentType: AgentType): AgentVersionStatus {
    return Computer.getAgent(computer, agentType)?.versionStatus;
  }

  static getFirstBuildTypeValue(computer: Computer, agentTypes: AgentType[]): BuildType {
    const buildTypeName = computer?.apps?.filter((app) => agentTypes.includes(app.applicationId))?.find(Boolean)?.buildType;
    return buildTypeName ? BuildType[buildTypeName] : null;
  }

  static IsSupportedAgentVersion(computer: Computer, agentType: AgentType, requiredVersion: number, needFull = false): boolean {
    const version = Computer.getAgentVersion(computer, agentType, needFull);

    if (needFull && version && requiredVersion) {
      const strVer1 = version.toString();
      const strVer2 = requiredVersion.toString();

      if (strVer2.length < strVer1.length) return +strVer1.substring(0, strVer2.length) >= requiredVersion;
    }

    return version >= requiredVersion;
  }

  static IsUnsupportedAgentVersion(computer: Computer, agentType: AgentType, requiredVersion: number, needFull = false): boolean {
    return Computer.hasAgent(computer, agentType) && Computer.getAgentVersion(computer, agentType, needFull) <= requiredVersion;
  }

  static license(computer?: Computer, type?: ComputerLicenseType, license?: ExtendedComputerLicense) {
    const getValue = () => {
      if (license) {
        return license as ComputerLicense;
      }
      switch (type) {
        case ComputerLicenseType.RMM:
          return computer?.rmmLicense;
        case ComputerLicenseType.Connect:
          return computer?.connectLicense;
        default:
          return computer?.backupLicense;
      }
    };
    const checkLicense = () => {
      const license = getValue();

      return !!license && !license.isExpired;
    };
    const getStatus = (): ComputerLicenseStatus => {
      const license = getValue();

      if (!license) {
        return ComputerLicenseStatus.NoLicense;
      }

      return license.isExpired ? ComputerLicenseStatus.Expired : (license.status as unknown as ComputerLicenseStatus);
    };
    const getStatusTitle = (isIgnoredExpiredStatus = false): string => {
      const license = getValue();
      const status = getStatus();
      const key = ComputerLicenseStatusMap.get(status);

      if (
        [ComputerLicenseStatus.Paid, ComputerLicenseStatus.Free, ComputerLicenseStatus.Expired].includes(status) &&
        isIgnoredExpiredStatus &&
        license.isExpired
      ) {
        return '';
      }

      return key;
    };
    const getInfo = (i18nPipe: I18NextPipe, licenseName?: string, isIgnoredExpiredStatus = false) => {
      const licenseStatusTitle = i18nPipe.transform(getStatusTitle(isIgnoredExpiredStatus));
      const licenseNameInfo =
        getValue() && licenseName
          ? i18nPipe.transform('shared:computers.computerLicenseNameInfo', { licenseName: licenseName.trim() })
          : '';

      return licenseStatusTitle ? `${licenseNameInfo} ${licenseStatusTitle}.` : licenseNameInfo;
    };

    return {
      value: getValue,
      hasLicense: checkLicense,
      status: getStatus,
      info: getInfo,
      statusTitle: getStatusTitle
    };
  }

  static getBackupInfo(computer: Computer, i18nPipe: I18NextPipe): string {
    const version = Computer.getBackupVersion(computer);

    if (!version) {
      return null;
    }

    return `${i18nPipe.transform(Computer.getBackupDestination(computer))}, v${version}`;
  }

  static isBackupOnline(computer: Computer): boolean {
    return Computer.isAgentOnline(computer, AgentType.Backup);
  }

  static getBackupVersion(computer: Computer): string {
    return Computer.getAgent(computer, AgentType.Backup)?.version;
  }

  static getBackupShortenedVersion(computer: Computer): string {
    const backupVersion = Computer.getBackupVersion(computer);

    return backupVersion ? `v${backupVersion.split('.', 2).join('.')}` : null;
  }

  static getBackupDestination(computer: Computer): string {
    if (Computer.isDRServer(computer)) return 'shared:licenses.management.backups:drServer';
    if (Computer.isLinux(computer)) return 'shared:licenses.management.backups:forLinux';
    if (Computer.isWindows(computer)) return 'shared:licenses.management.backups:forWindows';
    if (Computer.isMac(computer)) return 'shared:licenses.management.backups:forMac';
    return 'shared:licenses.management.backups:forVM';
  }

  static isPending(computer: Computer): boolean {
    return computer?.authStatus === AuthStatus.Pending;
  }

  static isDeepInstComputer(computer: Computer): boolean {
    return computer?.hid.startsWith('DeepInstID-') ?? false;
  }

  static isAgentSupportOfflineLicenseManagement(computer: Computer): boolean {
    return Computer.IsSupportedAgentVersion(
      computer,
      AgentType.Backup,
      Computer.isWindows(computer) ? WindowsAgentVersionSupportsOfflineLicenseManagement : UnixAgentVersionSupportsOfflineLicenseManagement
    );
  }
}

export class ComputersResponse extends PagedResponse<Computer> {}

export class ComputerTagDto {
  id: number;
  name: string;
  color?: TagColor;
}

export class ComputerWithStatus extends Computer {
  lastStatus?: string;
  lastStatusDate?: string;
}

export enum AgentType {
  RA = 'ra',
  Backup = 'backup',
  RMM = 'rmm',
  DeepInst = 'deepinst',
  NetworkDiscovery = 'nd'
}

export const AgentTypeAny = '@any';

export type BulkAgentType = AgentType | typeof AgentTypeAny;

export enum AgentState {
  Disabled = 0,
  Enabled = 1,
  Deleted = 2,
  NotRegistered = 3
}

export interface Agent {
  applicationId: AgentType;
  applicationState: AgentState;
  buildType: keyof typeof BuildType;
  MBSBuildType: number;
  displayVersion: string;
  online: boolean;
  version: string;
  versionStatus?: AgentVersionStatus;
  stateChangedBy?: string;
  stateChangedDate?: string;
  errorCode?: number;
  errorMessage?: string;
  isBeta?: boolean;
  betaExpiredDate: string;
}

export enum AgentVersionStatus {
  Latest = 'Latest',
  Outdated = 'Outdated',
  Unsupported = 'Unsupported'
}

export interface Edition {
  buildName: string;
  buildValue: number;
  isCurrentBuild: boolean;
}

export enum DisplayAgentType {
  Backup = 'Backup',
  RMM = 'RMM',
  RA = 'Connect',
  DeepInst = 'Deep Instinct'
}

export const agentTypeAdapter = [
  { agentType: AgentType.Backup, displayAgentType: DisplayAgentType.Backup },
  { agentType: AgentType.RMM, displayAgentType: DisplayAgentType.RMM },
  { agentType: AgentType.RA, displayAgentType: DisplayAgentType.RA },
  { agentType: AgentType.DeepInst, displayAgentType: DisplayAgentType.DeepInst }
];

export function convertAgentTypeToNeededCase(value: AgentType): string {
  return agentTypeAdapter.find((agentType) => agentType.agentType === value)?.displayAgentType;
}

export enum ComputersHealthFilterType {
  All = 'all',
  BackupSuccess = 'backup_success',
  BackupWarning = 'backup_warning',
  BackupFailed = 'backup_failed',
  BackupOverdue = 'backup_overdue',
  RMMProblem = 'rmm_problem',
  RMMWarning = 'rmm_warning',
  Pending = 'pending',
  Unsupported = 'unsupported',
  Outdated = 'outdated',
  InstallIssues = 'install_issues'
}

export interface ComputersHealthStatistics {
  all: number;
  backup_warning: number;
  backup_failed: number;
  backup_overdue: number;
  rmm_problem: number;
  rmm_warning: number;
  pending: number;
  unsupported: number;
  outdated: number;
  install_issues: number;
  loading: boolean;
}

export enum ComputerBackupRestoreStatus {
  Success = 'success',
  Warning = 'warning',
  Overdue = 'overdue',
  Error = 'error'
}

export enum ComputerStatus {
  Online = 'online',
  Offline = 'offline'
}

export interface UpdateAgentSettings {
  mbsBuildType: number;
  userId: string;
}

export enum VMDetectorInstance {
  Unknown = 0,
  PhysicalMachine = 1,
  VirtualMachine = 2,
  PhysicalMachineHyperVhost = 3,
  VMWare = 4,
  HyperV = 5,
  Kvm = 6,
  KvmQemu = 7,
  Xen = 8,
  Lxc = 9,
  Uml = 10,
  Virtuozzo = 11,
  AmazonV1 = 12,
  AmazonV2 = 13,
  Azure = 14,
  Google = 15,
  OpenStack = 16
}

export enum ComputersMode {
  Computers,
  Backup,
  RMM
}

export const ComputerModeAdapter = [
  { mode: ComputersMode.Backup, url: RoutingPath.ApBackupComputers, pageCaptionI18Key: 'modeBackup', agentType: AgentType.Backup },
  { mode: ComputersMode.RMM, url: RoutingPath.ApRMMComputers, pageCaptionI18Key: 'modeRMM', agentType: AgentType.RMM },
  { mode: ComputersMode.Computers, url: RoutingPath.ApComputers, pageCaptionI18Key: 'modeComputers', agentType: undefined }
].map((data) => ({
  ...data,
  displayAgentType: agentTypeAdapter.find((value) => value.agentType === data.agentType)?.displayAgentType
}));

export const ComputerModeAdapterByPanel = [
  { mode: ComputersMode.Backup, panel: SidepanelRouteType.Backup },
  { mode: ComputersMode.RMM, panel: SidepanelRouteType.RMM },
  { mode: ComputersMode.Computers, panel: SidepanelRouteType.ClientSettings }
];

export interface StorageConnection {
  id: string;
  displayName: string;
  isLocal: boolean;
  path: string;
  prefix: string;
}

export type ComputersFilters = {
  hid?: string;
  hids?: string[];
  companyIds?: string[];
  loadHidden?: boolean;
  loadActive?: boolean;
  loadDeleted?: boolean;
  online?: boolean;
  offline?: boolean;
  healthy?: boolean;
  healthWarning?: boolean;
  healthProblem?: boolean;
  type?: ComputersFiltersType;
  ipAddresses?: string[];
  appIds?: AgentType[];
  appIdsNotInstalled?: AgentType[];
  minAppVersion?: string;
  os?: OsType[];
  backupPlanStatuses?: ComputerBackupRestoreStatus[];
  restorePlanStatuses?: ComputerBackupRestoreStatus[];
  tags?: number[];
  search?: string;
  healthScopeAppId?: AgentType.Backup | AgentType.RMM;
  authStatus?: AuthStatus;
  installError?: boolean;
};

/** Include fields from Computer entity model */
export type ComputersModelPartialParams = {
  includes?: string | string[];
  withOnlineInfo?: boolean;
};

export enum ComputersFiltersType {
  None = 0,
  Unsupported
}

export type PlanSettingsReportParams = {
  email: string;
  hids?: string[];
  filter?: Partial<QuerySortParams> & Partial<QueryPagingParams> & ComputersFilters;
};

export type GetComputersParams = Partial<QuerySortParams> & Partial<QueryPagingParams> & ComputersFilters & ComputersModelPartialParams;

export enum ComputersPerPageSize {
  Small = 10,
  Medium = 25,
  Large = 50
}

export type SendLogsExtraOptions = {
  url?: string;
  title?: string;
  traceId?: string;
  message?: string;
  error?: string;
  lastRun?: string;
  plan?: string;
  ticket: string;
  description: string;
};

export class ComputersApplications {
  backup?: number;
  rmm?: number;
  ra?: number;
  deepInst?: number;
}

export enum ComputersAPIFilterKeys {
  Search = 'search',
  BackupPlanStatuses = 'backupPlanStatuses',
  RestorePlanStatuses = 'restorePlanStatuses',
  AppIds = 'appIds',
  AppIdsNotInstalled = 'appIdsNotInstalled',
  OS = 'os',
  Online = 'online',
  IpAddresses = 'ipAddresses',
  Tags = 'tags',
  LastLogin = 'lastLoggedUser'
}

export type ComputerSidePanelUriParams = SidePanelUriParams & { sidepanel?: SidepanelRouteType; planId?: string };

export type ComputerUriParams = ComputerSidePanelUriParams & {
  health?: string;
  hidden?: string;
  search?: string;
  company?: string;
  addNew?: string;
  apply?: boolean;
};

export type RmmCeLimit = {
  limitExceeded: boolean;
  limit: number;
};

export type AutoUpdatePayload = {
  filter: any;
  autoupdatedisabled: boolean;
  autoupdaterequest: { hid: string; autoupdatedisabled: boolean }[];
};

export type Volume = {
  allocatedSpace: number;
  usedSpace: number;
  maximumSpace: number;
  volumeName: string;
  storageVolumeName: string;
};

export type Snapshot = {
  snapshotId: string;
  originalVolumeName: string;
  creationTimestamp: string;
};

export type VssSettings = {
  isChangeStorageVolumeSupported: boolean;
  volumes: Volume[];
  snapshots: Snapshot[];
};

export enum ComputerMenuOption {
  SendLogs,
  ForceUpdate,
  EditAccount,
  EditOptions,
  OpenRmmInfo,
  AutoUpdate,
  HideFromList,
  RegenerateHID,
  UninstallAgent,
  DeleteComputer,
  OpenBackupPanel,
  ViewBackupHistory,
  InstallAgent,
  ChangeServiceAccount,
  EditNetworkCredentials,
  EditLocalFileSystemAccounts,
  OpenRestoreToCloudWizard,
  LogSummary
}

export const ComputerMenuOptionAdapter = [
  { option: ComputerMenuOption.SendLogs, handlerName: 'handleSendLogs' },
  { option: ComputerMenuOption.LogSummary, handlerName: 'handleLogSummary' },
  { option: ComputerMenuOption.ForceUpdate, handlerName: 'handleForceUpdate' },
  { option: ComputerMenuOption.EditAccount, handlerName: 'handleEditAccount' },
  { option: ComputerMenuOption.EditOptions, handlerName: 'handleEditOptions' },
  { option: ComputerMenuOption.OpenRmmInfo, handlerName: 'handleOpenRmmInfo' },
  { option: ComputerMenuOption.AutoUpdate, handlerName: 'handleAutoUpdate' },
  { option: ComputerMenuOption.HideFromList, handlerName: 'handleHideFromList' },
  { option: ComputerMenuOption.RegenerateHID, handlerName: 'handleRegenerateHID' },
  { option: ComputerMenuOption.UninstallAgent, handlerName: 'handleUninstallAgent' },
  { option: ComputerMenuOption.DeleteComputer, handlerName: 'handleDeleteComputer' },
  { option: ComputerMenuOption.OpenBackupPanel, handlerName: 'handleOpenBackupPanel' },
  { option: ComputerMenuOption.ViewBackupHistory, handlerName: 'handleViewBackupHistory' },
  { option: ComputerMenuOption.ChangeServiceAccount, handlerName: 'openChangeServiceAccount' },
  { option: ComputerMenuOption.EditNetworkCredentials, handlerName: 'openEditNetworkCredentials' },
  { option: ComputerMenuOption.EditLocalFileSystemAccounts, handlerName: 'handleEditLocalFileSystemAccounts' }
];
